<template>
  <div class="page-outer">
    <div class="page-header page-header-fixed-form">
      <div class="header-left">
        <h1 class="page-title">{{ pageTitle }}</h1>
      </div>
      <div class="header-actions">
        <button 
                class="btn btn-primary" 
                @click="handleCreate"
              >
                <i class="ri-add-line"></i>
                新增隱私權政策
              </button>
      </div>
    </div>

    <div class="page-container">
    <div class="page-toolbar">
      <div class="search-box">
        <input
          v-model="searchQuery"
          type="text"
          class="form-control"
          placeholder="搜尋版本或標題..."
          @input="handleSearch"
        />
        <i class="ri-search-line"></i>
      </div>
      <div class="filter-group">
        <select
          v-model="selectedStatus"
          class="form-control"
          @change="handleStatusFilter"
        >
          <option value="">全部狀態</option>
          <option value="draft">草稿</option>
          <option value="published">已發布</option>
          <option value="archived">已歸檔</option>
        </select>
      </div>
      <div class="filter-group">
        <select
          v-model="selectedLanguage"
          class="form-control"
          @change="handleLanguageFilter"
        >
          <option value="">全部語言</option>
          <option value="zh-TW">繁體中文</option>
          <option value="zh-CN">簡體中文</option>
          <option value="en">English</option>
        </select>
      </div>
    </div>

    <div v-if="loading" class="loading-state">
      <div class="spinner"></div>
      <p>載入中...</p>
    </div>

    <div v-else-if="error" class="error-state">
      <p class="error-message">{{ error }}</p>
      <button class="btn btn-secondary" @click="fetchPolicies">重試</button>
    </div>

    <div v-else class="table-wrapper">
      <table>
        <thead>
          <tr>
            <th>版本</th>
            <th>標題</th>
            <th>語言</th>
            <th>狀態</th>
            <th>生效日期</th>
            <th>發布時間</th>
            <th>建立時間</th>
            <th>操作</th>
          </tr>
        </thead>
        <tbody>
          <tr v-if="policies.length === 0">
            <td colspan="8" class="empty-state">
              沒有找到隱私權政策
            </td>
          </tr>
          <tr v-for="policy in policies" :key="policy.id">
            <td>{{ policy.version }}</td>
            <td>{{ policy.title }}</td>
            <td>{{ policy.language || 'zh-TW' }}</td>
            <td>
              <span :class="getStatusBadgeClass(policy.status)">
                {{ getStatusText(policy.status) }}
              </span>
            </td>
            <td>{{ formatDate(policy.effectiveDate) }}</td>
            <td>{{ formatDate(policy.publishedAt) }}</td>
            <td>{{ formatDate(policy.createdAt) }}</td>
            <td>
              <div class="action-buttons">
                <button
                  class="btn btn-sm btn-secondary"
                  @click="handleEdit(policy)"
                  title="編輯"
                >
                  <i class="ri-edit-line"></i>
                </button>
                <button
                  v-if="policy.status !== 'published'"
                  class="btn btn-sm btn-success"
                  @click="handlePublish(policy)"
                  title="發布"
                >
                  <i class="ri-send-plane-line"></i>
                </button>
                <button
                  class="btn btn-sm btn-danger"
                  @click="handleDelete(policy)"
                  title="刪除"
                >
                  <i class="ri-delete-bin-line"></i>
                </button>
              </div>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <ListPagination
      v-if="total > 0"
      :page="currentPage"
      :total-pages="totalPages"
      :total="total"
      @change="goToPage"
    />
    </div>
  </div>
</template>

<script>
import { ref, onMounted, computed } from 'vue';
import { useRouter } from 'vue-router';
import { usePageTitle } from '@/composables/usePageTitle.js';
import { policyService } from '@/api/modules/policies.js';
import { showSuccess, showError, showDeleteConfirm } from '@/utils/swal.js';

export default {
  name: 'PolicyList',
  setup() {
    const router = useRouter();
    const { pageTitle } = usePageTitle('隱私權政策列表');
    const policies = ref([]);
    const loading = ref(false);
    const error = ref(null);
    const searchQuery = ref('');
    const selectedStatus = ref('');
    const selectedLanguage = ref('');
    const currentPage = ref(1);
    const pageSize = ref(20);
    const total = ref(0);
    const searchTimeout = ref(null);

    const totalPages = computed(() => {
      return Math.ceil(total.value / pageSize.value);
    });

    const fetchPolicies = async () => {
      loading.value = true;
      error.value = null;

      try {
        const params = {
          page: Number(currentPage.value),
          pageSize: Number(pageSize.value),
        };

        if (searchQuery.value && searchQuery.value.trim().length > 0) {
          params.search = searchQuery.value.trim();
        }

        if (selectedStatus.value) {
          params.status = selectedStatus.value;
        }

        if (selectedLanguage.value) {
          params.language = selectedLanguage.value;
        }

        const response = await policyService.getPolicies(params);

        if (response && response.success && response.data) {
          if (response.data.items) {
            policies.value = response.data.items || [];
            total.value = response.data.pagination?.totalItems || response.data.pagination?.total || 0;
          } else if (Array.isArray(response.data)) {
            policies.value = response.data;
            total.value = response.data.length;
          }
        } else {
          error.value = response && response.message || '獲取隱私權政策列表失敗';
        }
      } catch (err) {
        console.error('獲取隱私權政策列表失敗:', err);
        error.value = err.message || '獲取隱私權政策列表失敗';
      } finally {
        loading.value = false;
      }
    };

    const handleStatusFilter = () => {
      currentPage.value = 1;
      fetchPolicies();
    };

    const handleLanguageFilter = () => {
      currentPage.value = 1;
      fetchPolicies();
    };

    const handleSearch = () => {
      if (searchTimeout.value) {
        clearTimeout(searchTimeout.value);
      }
      searchTimeout.value = setTimeout(() => {
        currentPage.value = 1;
        fetchPolicies();
      }, 500);
    };

    const goToPage = (page) => {
      if (page >= 1 && page <= totalPages.value) {
        currentPage.value = page;
        fetchPolicies();
      }
    };

    const handleCreate = () => {
      router.push('/client/policies/create');
    };

    const handleEdit = (policy) => {
      router.push(`/client/policies/${policy.id}/edit`);
    };

    const handlePublish = async (policy) => {
      try {
        const response = await policyService.publishPolicy(policy.id);
        if (response && response.success) {
          await showSuccess('發布成功', `隱私權政策「${policy.title}」已成功發布`);
          await fetchPolicies();
        } else {
          await showError('發布失敗', response?.message || '發布失敗');
        }
      } catch (err) {
        console.error('發布失敗:', err);
        await showError('發布失敗', err.message || '發布失敗，請稍後再試');
      }
    };

    const handleDelete = async (policy) => {
      const confirmed = await showDeleteConfirm(`${policy.title} (版本: ${policy.version})`);
      
      if (!confirmed) {
        return;
      }

      const originalLoading = loading.value;
      loading.value = true;
      error.value = null;

      try {
        const response = await policyService.deletePolicy(policy.id);

        if (response && response.success) {
          await showSuccess('刪除成功', `隱私權政策「${policy.title}」已成功刪除`);
          
          if (policies.value.length === 1 && currentPage.value > 1) {
            currentPage.value = currentPage.value - 1;
          }
          
          await fetchPolicies();
        } else {
          const errorMsg = response?.message || '刪除隱私權政策失敗';
          await showError('刪除失敗', errorMsg);
        }
      } catch (err) {
        console.error('刪除隱私權政策失敗:', err);
        await showError('刪除失敗', err.message || '刪除失敗，請稍後再試');
      } finally {
        loading.value = originalLoading;
      }
    };

    const formatDate = (dateString) => {
      if (!dateString) return '-';
      const date = new Date(dateString);
      return date.toLocaleDateString('zh-TW');
    };

    const getStatusBadgeClass = (status) => {
      const statusMap = {
        'draft': 'badge badge-secondary',
        'published': 'badge badge-success',
        'archived': 'badge badge-danger'
      };
      return statusMap[status] || 'badge badge-secondary';
    };

    const getStatusText = (status) => {
      const statusMap = {
        'draft': '草稿',
        'published': '已發布',
        'archived': '已歸檔'
      };
      return statusMap[status] || status;
    };

    onMounted(() => {
      fetchPolicies();
    });

    return {
      policies,
      loading,
      error,
      searchQuery,
      selectedStatus,
      selectedLanguage,
      currentPage,
      totalPages,
      total,
      pageTitle,
      handleSearch,
      handleStatusFilter,
      handleLanguageFilter,
      goToPage,
      handleCreate,
      handleEdit,
      handlePublish,
      handleDelete,
      formatDate,
      getStatusBadgeClass,
      getStatusText,
      fetchPolicies,
    };
  },
};
</script>

<style scoped>
@import '~@/assets/scss/components/_list-page-common.scss';

.table-wrapper table {
  min-width: 1000px;
}
</style>
